import { formatDate } from "@certn/utils";
import { css } from "@emotion/css";
import { endOfMonth, getDaysInMonth } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { useLocale } from "react-aria";

import { DateSelectSegment } from "./DateSelectSegment";
import { Granularity } from "./constants";
import { getDisplayNames } from "./utils";
import { Stack } from "../../../foundations/grid-layout/Stack";
import {
  localeString,
  useDateFNSLocale,
} from "../../../hooks/useDateFNSLocale";
import { _Text } from "../../_assets/_Text";
import { LinkButton } from "../../link/LinkButton";
import { DialogTrigger } from "../../popover/DialogTrigger";
import {
  FieldErrorsProps,
  FieldErrors,
} from "../_assets/input-wrapper/FieldErrors";
import { FormLabelRAC } from "../_assets/input-wrapper/FormLabelRAC";

export type DateSelectValue = {
  year: number;
  month: number;
  day: number;
};

export interface DateSelectorProps {
  name?: string;
  label: string;
  formatOverride?: string;
  onChange: (value: DateSelectValue) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  errors?: FieldErrorsProps["children"];
  testID: string;
  value?: DateSelectValue;
  /**
   * When true, shows only future years (100 years into the future).
   * When false/undefined, shows only past years (100 years into the past).
   * @see allowPastAndFutureDates for showing both directions.
   */
  isFutureDate?: boolean;
  /**
   * When true, shows years in both directions (50 years past and 50 years future).
   * Useful for fields like expiration dates where users may need to enter both
   * expired and future dates. Takes precedence over `isFutureDate`.
   */
  allowPastAndFutureDates?: boolean;
  isDisabled?: boolean;
  isRequired?: boolean;
  granularity?: Granularity;
  /**
   * Used when the `granularity` is reduced. If that's `"month"` and `roundUp` is `true`, the day will be set to the last day of the month, otherwise the first.
   */
  roundUp?: boolean;
  /**
   * @note Currently setting this doesn't affect validation, this was added to handle an edge case with `roundUp`.
   */
  maxDate?: Date;
  fullWidth?: boolean;
  isInvalid?: boolean;
  showFormattedDate?: boolean;
  popoverLabel?: React.ReactNode;
  /**
   * A `Popover` component.
   */
  popover?: React.ReactNode;
}

const now = new Date();
const currentYear = now.getUTCFullYear();

export function DateSelector(props: DateSelectorProps) {
  const {
    value = { day: 0, month: 0, year: 0 },
    showFormattedDate = false,
    roundUp = false,
  } = props;
  const { locale } = useLocale();
  const { dateFNSLocale } = useDateFNSLocale(locale as localeString);
  const [formatOrder, setFormatOrder] = useState<string[]>([]);
  const [focused, setFocused] = useState<Record<string, boolean | null>>({
    year: null,
    month: null,
    day: null,
  });

  const displayNames = getDisplayNames(locale);

  // currently there's only "month" which hides day selector
  // we can extend this component to use other formats as designs roll in
  const isDayHidden = props.granularity === Granularity.month;
  const formattedDate = formatDate(
    value.day && value.month && value.year
      ? new Date(value.year, value.month - 1, value.day)
      : null,
    isDayHidden ? "MMMM yyyy" : "PPP",
    "",
    locale
  );

  const yearPlaceholder = displayNames?.of("year") || "Year";
  const monthPlaceholder = displayNames?.of("month") || "Month";
  const dayPlaceholder = displayNames?.of("day") || "Day";

  useEffect(() => {
    const componentIsTouched = Object.values(focused).some(
      (focus) => focus !== null
    );
    const componentIsFocused = Object.values(focused).some(
      (focus) => focus === true
    );

    if (componentIsFocused) {
      props.onFocus && props.onFocus();
    } else if (componentIsTouched) {
      props.onBlur && props.onBlur();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focused]);

  const calculateDay = (month: number, year: number) => {
    if (props.granularity === Granularity.month) {
      const calculatedDate = new Date(year, month - 1);

      calculatedDate.setDate(
        roundUp ? endOfMonth(calculatedDate).getDate() : 1
      );
      if (props.maxDate && calculatedDate > props.maxDate) {
        return 1;
      }
      return calculatedDate.getDate();
    }

    return value.day > fetchDaysInMonth(month, year) ? 0 : value.day;
  };
  const handleSetYear = (year: number) => {
    props.onChange({
      ...value,
      year,
      day: calculateDay(value.month, year),
    });
  };

  const handleSetMonth = (month: number) => {
    props.onChange({
      ...value,
      month,
      day: calculateDay(month, value.year),
    });
  };

  const handleSetDay = (day: number) => {
    props.onChange({ ...value, day });
  };

  const fetchDaysInMonth = (month: number, year: number) => {
    if (!month && !year) return 0;
    return getDaysInMonth(new Date(year, month - 1, 1));
  };

  // set formatOrder based on override or locale language specific ordering
  useEffect(() => {
    const format = props.formatOverride
      ? props.formatOverride.toLowerCase()
      : dateFNSLocale?.formatLong?.date({ width: "short" }).toLowerCase();

    // remove whitespaces and split by various locale separating chars
    // ex: "hu-HU": "yyyy. MM. dd." or "es-PA": "MM/dd/yyyy"
    setFormatOrder(
      format
        .replace(/\s/g, "") // remove spaces
        .replace(/([ymd])(?=.*\1)/g, "") // remove repeated characters
        .split(/[.,/-]/) // split into array for ordering
    );
  }, [props.formatOverride, dateFNSLocale?.formatLong]);

  // calculates and fills array with years based on date direction props
  const years = useMemo(() => {
    // allowPastAndFutureDates: show 50 years in each direction (for fields like expiration dates)
    if (props.allowPastAndFutureDates) {
      const futureYears = Array.from(
        { length: 50 },
        (_, idx) => currentYear + idx
      );
      const pastYears = Array.from(
        { length: 50 },
        (_, idx) => currentYear - idx - 1
      );
      // Sort in descending order for consistency with the default past-years behavior
      return [...futureYears, ...pastYears].sort((a, b) => b - a);
    }
    // isFutureDate: show 100 years into the future only
    if (props.isFutureDate) {
      return Array.from({ length: 100 }, (_, idx) => currentYear + idx);
    }
    // Default: show 100 years in the past only
    return Array.from({ length: 100 }, (_, idx) => currentYear - idx);
  }, [props.allowPastAndFutureDates, props.isFutureDate]);

  // builds month array with locale specific months
  const months = useMemo(
    () =>
      Array.from({ length: 12 }, (_, idx) => {
        const month = dateFNSLocale?.localize?.month(idx);
        return month.charAt(0).toUpperCase() + month.slice(1);
      }),
    [dateFNSLocale?.localize]
  );

  // builds days array based on month and year selected
  const days = useMemo(() => {
    const daysInMonth =
      value.year && value.month
        ? fetchDaysInMonth(value.month, value.year)
        : 31;

    return Array.from({ length: daysInMonth }, (_, idx) => idx + 1);
  }, [value]);

  const segmentLookup = (input: string, index: number) => {
    switch (input) {
      case "m":
        return (
          <div key={index} className={styles.month}>
            <DateSelectSegment
              testID={`${props.testID}-month`}
              isDisabled={props.isDisabled}
              selectedKey={value?.month}
              input={input}
              setValue={handleSetMonth}
              options={months}
              placeholder={
                monthPlaceholder?.charAt(0).toUpperCase() +
                monthPlaceholder?.slice(1)
              }
              isInvalid={
                props.isInvalid || (focused["month"] === false && !value.month)
              }
              onFocus={() => setFocused((curr) => ({ ...curr, month: true }))}
              onBlur={() =>
                setTimeout(() =>
                  setFocused((curr) => ({ ...curr, month: false }))
                )
              }
              isRequired={props.isRequired}
            />
          </div>
        );
      case "d":
        if (isDayHidden) return null;
        return (
          <div key={index}>
            <DateSelectSegment
              testID={`${props.testID}-day`}
              isDisabled={props.isDisabled}
              selectedKey={value.day}
              input={input}
              setValue={handleSetDay}
              options={days}
              placeholder={
                dayPlaceholder?.charAt(0).toUpperCase() +
                dayPlaceholder?.slice(1)
              }
              isInvalid={
                props.isInvalid || (focused["day"] === false && !value.day)
              }
              onFocus={() => setFocused((curr) => ({ ...curr, day: true }))}
              onBlur={() =>
                setTimeout(() =>
                  setFocused((curr) => ({ ...curr, day: false }))
                )
              }
              isRequired={props.isRequired}
            />
          </div>
        );
      case "y":
        return (
          <div key={index} className={styles.year}>
            <DateSelectSegment
              testID={`${props.testID}-year`}
              isDisabled={props.isDisabled}
              selectedKey={value.year}
              input={input}
              setValue={handleSetYear}
              options={years}
              placeholder={
                yearPlaceholder?.charAt(0).toUpperCase() +
                yearPlaceholder?.slice(1)
              }
              isInvalid={
                props.isInvalid || (focused["year"] === false && !value.year)
              }
              onFocus={() => setFocused((curr) => ({ ...curr, year: true }))}
              onBlur={() =>
                setTimeout(() =>
                  setFocused((curr) => ({ ...curr, year: false }))
                )
              }
              isRequired={props.isRequired}
            />
          </div>
        );
    }
  };

  return (
    <Stack gap="space-1">
      <div className={styles.labelWrapper}>
        <FormLabelRAC isRequired={props.isRequired}>{props.label}</FormLabelRAC>
        {props.popoverLabel && (
          <DialogTrigger>
            <LinkButton testID="open-popover-link">
              {props.popoverLabel}
            </LinkButton>
            {props.popover}
          </DialogTrigger>
        )}
      </div>
      <div className={styles.inputsWrapper}>
        {formatOrder.map((input: string, index: number) =>
          segmentLookup(input, index)
        )}
      </div>
      {formattedDate && showFormattedDate && (
        <_Text style="input/helper" color="text-weak">
          {formattedDate}
        </_Text>
      )}
      <FieldErrors>{props.errors}</FieldErrors>
    </Stack>
  );
}

const styles = {
  errorWrapper: css({ paddingTop: "var(--space-1)" }),
  labelWrapper: css({
    display: "flex",
    justifyContent: "space-between",
  }),
  inputsWrapper: css({
    width: "100%",
    display: "flex",
    gap: "var(--space-2)",
  }),
  year: css({
    flex: "1.25",
  }),
  month: css({
    flex: "2",
  }),
};
